/**
 * CustomDateDropdown Component
 *
 * Tabbed dropdown for custom date selection with Fixed, Since, and Last tabs.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { formatDateForCube, convertDateRangeTypeToValue } from '../shared/utils.js'
import { useTranslation } from '../../hooks/useTranslation.js'

type TabType = 'fixed' | 'since' | 'last'
type LastUnit = 'days' | 'weeks' | 'months' | 'quarters' | 'years'

interface CustomDateDropdownProps {
  isOpen: boolean
  onClose: () => void
  onDateRangeChange: (dateRange: string | string[]) => void
  currentDateRange?: string | string[]
  anchorRef: React.RefObject<HTMLElement>
}

const LAST_UNITS: { value: LastUnit; label: string }[] = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'quarters', label: 'Quarters' },
  { value: 'years', label: 'Years' }
]

const CustomDateDropdown: React.FC<CustomDateDropdownProps> = ({
  isOpen,
  onClose,
  onDateRangeChange,
  currentDateRange
}) => {
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('fixed')

  // Fixed tab state
  const [fixedStartDate, setFixedStartDate] = useState('')
  const [fixedEndDate, setFixedEndDate] = useState('')

  // Since tab state
  const [sinceDate, setSinceDate] = useState('')

  // Last tab state
  const [lastNumber, setLastNumber] = useState(7)
  const [lastUnit, setLastUnit] = useState<LastUnit>('days')

  // Initialize state from current date range
  useEffect(() => {
    if (!currentDateRange) return

    if (Array.isArray(currentDateRange)) {
      // Custom date range - set fixed tab
      setActiveTab('fixed')
      setFixedStartDate(currentDateRange[0] || '')
      setFixedEndDate(currentDateRange[1] || currentDateRange[0] || '')
    } else {
      // Check for "last N units" pattern
      const lastNMatch = currentDateRange.match(/^last\s+(\d+)\s+(day|days|week|weeks|month|months|quarter|quarters|year|years)$/i)
      if (lastNMatch) {
        setActiveTab('last')
        setLastNumber(parseInt(lastNMatch[1], 10))
        const unit = lastNMatch[2].toLowerCase()
        if (unit === 'day') setLastUnit('days')
        else if (unit === 'week') setLastUnit('weeks')
        else if (unit === 'month') setLastUnit('months')
        else if (unit === 'quarter') setLastUnit('quarters')
        else if (unit === 'year') setLastUnit('years')
        else setLastUnit(unit.endsWith('s') ? unit as LastUnit : `${unit}s` as LastUnit)
      }
    }
  }, [currentDateRange])

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    // Delay adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Handle apply for Fixed tab
  const handleApplyFixed = useCallback(() => {
    if (fixedStartDate && fixedEndDate) {
      onDateRangeChange([fixedStartDate, fixedEndDate])
    } else if (fixedStartDate) {
      onDateRangeChange([fixedStartDate, fixedStartDate])
    }
  }, [fixedStartDate, fixedEndDate, onDateRangeChange])

  // Handle apply for Since tab
  const handleApplySince = useCallback(() => {
    if (sinceDate) {
      const today = formatDateForCube(new Date())
      onDateRangeChange([sinceDate, today])
    }
  }, [sinceDate, onDateRangeChange])

  // Handle apply for Last tab
  const handleApplyLast = useCallback(() => {
    if (lastNumber > 0) {
      // Convert to the internal DateRangeType format then to value
      const rangeType = `last_n_${lastUnit}`
      const value = convertDateRangeTypeToValue(rangeType, lastNumber)
      onDateRangeChange(value)
    }
  }, [lastNumber, lastUnit, onDateRangeChange])

  if (!isOpen) return null

  const tabButtonStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? 'var(--dc-primary)' : 'transparent',
    color: isActive ? 'white' : 'var(--dc-text)',
    borderBottom: isActive ? 'none' : `2px solid var(--dc-border)`
  })

  // Stop propagation to prevent parent handlers from interfering
  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      ref={dropdownRef}
      className="dc:absolute dc:top-full dc:left-0 dc:mt-1 dc:z-50 dc:border dc:rounded-lg dc:shadow-lg dc:min-w-[280px]"
      style={{
        backgroundColor: 'var(--dc-surface)',
        borderColor: 'var(--dc-border)',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
      }}
      onClick={handleDropdownClick}
      onMouseDown={handleDropdownClick}
    >
      {/* Tab Headers */}
      <div
        className="dc:flex dc:border-b"
        style={{ borderColor: 'var(--dc-border)' }}
      >
        {(['fixed', 'since', 'last'] as TabType[]).map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className="dc:flex-1 dc:px-4 dc:py-2 dc:text-sm dc:font-medium dc:capitalize dc:transition-colors"
            style={tabButtonStyle(activeTab === tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="dc:p-4">
        {/* Fixed Tab */}
        {activeTab === 'fixed' && (
          <div className="dc:space-y-3">
            <div>
              <label
                className="dc:block dc:text-xs dc:font-medium dc:mb-1"
                style={{ color: 'var(--dc-text-secondary)' }}
              >
                {t('dashboardFilter.customDate.startDate')}
              </label>
              <input
                type="date"
                value={fixedStartDate}
                onChange={(e) => setFixedStartDate(e.target.value)}
                className="dc:w-full dc:px-3 dc:py-2 dc:text-sm dc:border dc:rounded dc:focus:outline-none dc:focus:ring-2"
                style={{
                  borderColor: 'var(--dc-border)',
                  backgroundColor: 'var(--dc-bg)',
                  color: 'var(--dc-text)'
                }}
              />
            </div>
            <div>
              <label
                className="dc:block dc:text-xs dc:font-medium dc:mb-1"
                style={{ color: 'var(--dc-text-secondary)' }}
              >
                {t('dashboardFilter.customDate.endDate')}
              </label>
              <input
                type="date"
                value={fixedEndDate}
                onChange={(e) => setFixedEndDate(e.target.value)}
                className="dc:w-full dc:px-3 dc:py-2 dc:text-sm dc:border dc:rounded dc:focus:outline-none dc:focus:ring-2"
                style={{
                  borderColor: 'var(--dc-border)',
                  backgroundColor: 'var(--dc-bg)',
                  color: 'var(--dc-text)'
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleApplyFixed}
              disabled={!fixedStartDate}
              className="dc:w-full dc:py-2 dc:text-sm dc:font-medium dc:rounded dc:transition-colors dc:disabled:opacity-50 dc:disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--dc-primary)',
                color: 'white'
              }}
            >
              {t('common.actions.apply')}
            </button>
          </div>
        )}

        {/* Since Tab */}
        {activeTab === 'since' && (
          <div className="dc:space-y-3">
            <div>
              <label
                className="dc:block dc:text-xs dc:font-medium dc:mb-1"
                style={{ color: 'var(--dc-text-secondary)' }}
              >
                {t('dashboardFilter.customDate.sinceDate')}
              </label>
              <input
                type="date"
                value={sinceDate}
                onChange={(e) => setSinceDate(e.target.value)}
                className="dc:w-full dc:px-3 dc:py-2 dc:text-sm dc:border dc:rounded dc:focus:outline-none dc:focus:ring-2"
                style={{
                  borderColor: 'var(--dc-border)',
                  backgroundColor: 'var(--dc-bg)',
                  color: 'var(--dc-text)'
                }}
              />
            </div>
            <p
              className="dc:text-xs"
              style={{ color: 'var(--dc-text-secondary)' }}
            >
              {t('dashboardFilter.customDate.fromSelectedToToday')}
            </p>
            <button
              type="button"
              onClick={handleApplySince}
              disabled={!sinceDate}
              className="dc:w-full dc:py-2 dc:text-sm dc:font-medium dc:rounded dc:transition-colors dc:disabled:opacity-50 dc:disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--dc-primary)',
                color: 'white'
              }}
            >
              {t('common.actions.apply')}
            </button>
          </div>
        )}

        {/* Last Tab */}
        {activeTab === 'last' && (
          <div className="dc:space-y-3">
            <div className="dc:flex dc:gap-2">
              <div className="dc:flex-1">
                <label
                  className="dc:block dc:text-xs dc:font-medium dc:mb-1"
                  style={{ color: 'var(--dc-text-secondary)' }}
                >
                  {t('dashboardFilter.customDate.number')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={lastNumber}
                  onChange={(e) => setLastNumber(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="dc:w-full dc:px-3 dc:py-2 dc:text-sm dc:border dc:rounded dc:focus:outline-none dc:focus:ring-2"
                  style={{
                    borderColor: 'var(--dc-border)',
                    backgroundColor: 'var(--dc-bg)',
                    color: 'var(--dc-text)'
                  }}
                />
              </div>
              <div className="dc:flex-1">
                <label
                  className="dc:block dc:text-xs dc:font-medium dc:mb-1"
                  style={{ color: 'var(--dc-text-secondary)' }}
                >
                  {t('dashboardFilter.customDate.unit')}
                </label>
                <select
                  value={lastUnit}
                  onChange={(e) => setLastUnit(e.target.value as LastUnit)}
                  className="dc:w-full dc:px-3 dc:py-2 dc:text-sm dc:border dc:rounded dc:focus:outline-none dc:focus:ring-2"
                  style={{
                    borderColor: 'var(--dc-border)',
                    backgroundColor: 'var(--dc-bg)',
                    color: 'var(--dc-text)'
                  }}
                >
                  {LAST_UNITS.map(unit => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p
              className="dc:text-xs"
              style={{ color: 'var(--dc-text-secondary)' }}
            >
              {t('dashboardFilter.customDate.lastNPreview', { number: lastNumber, unit: lastNumber === 1 ? lastUnit.slice(0, -1) : lastUnit })}
            </p>
            <button
              type="button"
              onClick={handleApplyLast}
              disabled={lastNumber < 1}
              className="dc:w-full dc:py-2 dc:text-sm dc:font-medium dc:rounded dc:transition-colors dc:disabled:opacity-50 dc:disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--dc-primary)',
                color: 'white'
              }}
            >
              {t('common.actions.apply')}
            </button>
          </div>
        )}
      </div>

      {/* Cancel Button */}
      <div
        className="dc:px-4 dc:pb-4"
      >
        <button
          type="button"
          onClick={onClose}
          className="dc:w-full dc:py-2 dc:text-sm dc:font-medium dc:rounded dc:border dc:transition-colors"
          style={{
            borderColor: 'var(--dc-border)',
            color: 'var(--dc-text-secondary)',
            backgroundColor: 'transparent'
          }}
        >
          {t('common.actions.cancel')}
        </button>
      </div>
    </div>
  )
}

export default CustomDateDropdown
